// Typed preferences — the web counterpart of PreferencesStore/PrefKeys (TrailBivvy/App/AppPreferences.swift).
// Small values live in localStorage so they are available synchronously at launch (no onboarding or paywall flash);
// everything heavier is in IndexedDB (src/storage/idb.ts). Each preference is a Preact signal, persisted on change
// (decisions.md PWA-23).

import { signal, effect, type Signal } from '@preact/signals';
import type { OvernightPreference } from '../core/models';

export interface TonightSettings {
  desiredStopHour?: number;
  desiredStopMinute: number;
  maxAdditionalWalkingHours: number;
  maxDetourMeters: number;
  preference: OvernightPreference;
  includeOptionsBehind: boolean;
}

export const defaultTonightSettings: TonightSettings = {
  desiredStopMinute: 0,
  maxAdditionalWalkingHours: 3,
  maxDetourMeters: 1000,
  preference: 'mixed',
  includeOptionsBehind: true,
};

/** Desired stop time today (epoch ms) relative to `now`, or undefined (then civil dusk applies). */
export function desiredStopTime(settings: TonightSettings, now: number): number | undefined {
  if (settings.desiredStopHour === undefined) return undefined;
  const d = new Date(now);
  d.setHours(settings.desiredStopHour, settings.desiredStopMinute, 0, 0);
  return d.getTime();
}

export type GPSPowerMode = 'batterySaver' | 'standard' | 'precise';
export type UnitSystemPreference = 'automatic' | 'metric' | 'imperial';
export type ThemePreference = 'system' | 'light' | 'dark';

const PREFIX = 'tb.';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T, fallback: T): void {
  try {
    if (value === undefined || JSON.stringify(value) === JSON.stringify(fallback)) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // storage full or blocked (private mode): the value lives for this session only
  }
}

function pref<T>(key: string, fallback: T): Signal<T> {
  const s = signal<T>(read(key, fallback));
  let first = true;
  effect(() => {
    const value = s.value;
    if (first) {
      first = false;
      return;
    }
    write(key, value, fallback);
  });
  return s;
}

/** Same keys as iOS PrefKeys where the meaning is the same. */
export const prefs = {
  onboardingCompleted: pref('onboardingCompleted', false),
  tonightSettings: pref<TonightSettings>('tonightSettings', defaultTonightSettings),
  /** WalkingPerformanceModel as stored by src/nav (opaque here). */
  walkingModel: pref<unknown>('walkingModel', undefined),
  offRouteConfiguration: pref<unknown>('offRouteConfiguration', undefined),
  gpsPowerMode: pref<GPSPowerMode>('gpsPowerMode', 'standard'),
  activeStageIDs: pref<string[]>('activeStageIDs', []),
  activeRouteReversed: pref('activeRouteReversed', false),
  manualOfflineMode: pref('manualOfflineMode', false),
  unitSystem: pref<UnitSystemPreference>('unitSystem', 'automatic'),
  activeTrailID: pref<string | undefined>('activeTrailID', undefined),
  navigationRunningAt: pref<number | undefined>('navigationRunningAt', undefined),
  /** Web only: language override (undefined = the browser's languages). */
  language: pref<string | undefined>('language', undefined),
  /** Web only: light/dark override. */
  theme: pref<ThemePreference>('theme', 'system'),
  /** Web only: map layer choices (src/map MapLayerPreferences, opaque here). */
  mapLayers: pref<unknown>('mapLayers', undefined),
  /** Web only: the address typed on the paywall, offered again for "restore by e-mail" (stays on this device). */
  subscriptionEmail: pref<string | undefined>('subscriptionEmail', undefined),
};

export type Prefs = typeof prefs;
